import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useEquipment } from '../../../context/EquipmentContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { EQUIPMENT_TYPES, EQUIPMENT_STATUS } from '../../../data/nurseAppModels';

const BorrowEquipmentPage = () => {
  const navigate = useNavigate();
  const { equipment, borrowEquipment } = useEquipment();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('AVAILABLE');
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [expectedReturnDate, setExpectedReturnDate] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter equipment
  const filteredEquipment = equipment.filter(eq => {
    const matchesType = eq.type === EQUIPMENT_TYPES.LARGE_ITEM;
    const matchesStatus = statusFilter === 'all' || eq.status === statusFilter;
    const matchesSearch = !searchTerm || 
      eq.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      eq.code?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesStatus && matchesSearch;
  });

  // Set default return date (3 days from now)
  useEffect(() => {
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 3);
    setExpectedReturnDate(defaultDate.toISOString().split('T')[0]);
  }, []);

  const handleSelectEquipment = (eq) => {
    if (eq.status !== EQUIPMENT_STATUS.AVAILABLE) {
      alert('อุปกรณ์นี้ไม่พร้อมใช้งาน');
      return;
    }
    setSelectedEquipment(eq);
  };

  const handleSubmit = async () => {
    if (!selectedEquipment) {
      alert('กรุณาเลือกอุปกรณ์ที่ต้องการยืม');
      return;
    }

    if (!expectedReturnDate) {
      alert('กรุณาเลือกวันที่คืนที่คาดหวัง');
      return;
    }

    const returnDate = new Date(expectedReturnDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (returnDate <= today) {
      alert('วันที่คืนต้องมากกว่าวันนี้');
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await borrowEquipment(selectedEquipment.id, {
        expectedReturnAt: returnDate,
        notes: notes.trim()
      });

      if (result.success) {
        alert('ส่งคำขอยืมสำเร็จ รอการอนุมัติจากหัวหน้าทีม');
        navigate('/nurse/equipment/my-borrows');
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error borrowing equipment:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      [EQUIPMENT_STATUS.AVAILABLE]: { bg: 'bg-green-100', text: 'text-green-800', label: '✅ พร้อมใช้' },
      [EQUIPMENT_STATUS.BORROWED]: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '📦 ถูกยืม' },
      [EQUIPMENT_STATUS.REPAIR]: { bg: 'bg-red-100', text: 'text-red-800', label: '🔧 อยู่ระหว่างส่งซ่อม' },
      [EQUIPMENT_STATUS.MAINTENANCE]: { bg: 'bg-blue-100', text: 'text-blue-800', label: '🛠️ บำรุงรักษา' },
      [EQUIPMENT_STATUS.DAMAGED]: { bg: 'bg-red-100', text: 'text-red-800', label: '⚠️ เสียหาย' }
    };
    return badges[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
  };

  if (!currentTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
          <Link
            to="/nurse/teams"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ไปเลือกทีม
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/nurse/equipment"
              className="text-gray-600 hover:text-gray-800"
            >
              ← กลับ
            </Link>
            <h1 className="text-2xl font-bold text-gray-800">ยืมอุปกรณ์</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Equipment List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">📦 เลือกอุปกรณ์ที่ต้องการยืม</h2>
              
              {/* Filters */}
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <input
                  type="text"
                  placeholder="ค้นหาอุปกรณ์..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">ทุกสถานะ</option>
                  <option value={EQUIPMENT_STATUS.AVAILABLE}>พร้อมใช้</option>
                  <option value={EQUIPMENT_STATUS.BORROWED}>ถูกยืม</option>
                  <option value={EQUIPMENT_STATUS.REPAIR}>อยู่ระหว่างส่งซ่อม</option>
                </select>
              </div>

              {/* Equipment List */}
              {filteredEquipment.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🩺</div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">ไม่พบอุปกรณ์</h3>
                  <p className="text-gray-600">ลองเปลี่ยนเงื่อนไขการค้นหาดู</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredEquipment.map(eq => {
                    const statusBadge = getStatusBadge(eq.status);
                    const isSelected = selectedEquipment?.id === eq.id;
                    const isAvailable = eq.status === EQUIPMENT_STATUS.AVAILABLE;

                    return (
                      <div
                        key={eq.id}
                        onClick={() => handleSelectEquipment(eq)}
                        className={`border rounded-lg p-4 cursor-pointer transition ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : isAvailable
                            ? 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                            : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-gray-800">{eq.name}</h3>
                              <span className={`px-2 py-1 text-xs rounded ${statusBadge.bg} ${statusBadge.text}`}>
                                {statusBadge.label}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-1">รหัส: {eq.code}</p>
                            {eq.currentLocation && (
                              <p className="text-sm text-gray-500">📍 {eq.currentLocation}</p>
                            )}
                          </div>
                          {isAvailable && (
                            <button
                              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                                isSelected
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              {isSelected ? '✓ เลือกแล้ว' : 'เลือก'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Selected Equipment Form */}
          <div className="lg:col-span-1">
            {selectedEquipment ? (
              <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
                <h2 className="text-lg font-bold text-gray-800 mb-4">📋 รายละเอียดการยืม</h2>
                
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🩺</span>
                    <div>
                      <h3 className="font-semibold text-gray-800">{selectedEquipment.name}</h3>
                      <p className="text-sm text-gray-600">{selectedEquipment.code}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📅 วันที่ยืม
                    </label>
                    <input
                      type="date"
                      value={new Date().toISOString().split('T')[0]}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📅 วันที่คืนที่คาดหวัง <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={expectedReturnDate}
                      onChange={(e) => setExpectedReturnDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📝 หมายเหตุ
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      placeholder="ระบุรายละเอียดการใช้งาน..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !expectedReturnDate}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'กำลังส่ง...' : 'ส่งคำขอ'}
                  </button>

                  <button
                    onClick={() => setSelectedEquipment(null)}
                    className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    ลบออก
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">📋</div>
                  <p className="text-gray-600">เลือกอุปกรณ์ที่ต้องการยืม</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BorrowEquipmentPage;

